import { jsx as _jsx } from "react/jsx-runtime";
import { useSwipeDown } from '@kubit/web-ui-components/hooks';
import { useContentVisibility } from '@kubit/web-ui-components/hooks/useContentVisibility';
import { useMediaDevice } from '@kubit/web-ui-components/hooks/useMediaDevice';
import { useScrollDetectionWithAutoFocus } from '@kubit/web-ui-components/hooks/useScrollDetectionWithAutoFocus';
import { useScrollEffect } from '@kubit/web-ui-components/hooks/useScrollEffect';
import { useStyles } from '@kubit/web-ui-components/hooks/useStyles';
import { DeviceBreakpointsType } from '@kubit/web-ui-components/types/breakpoints';
import * as React from 'react';
import { STYLES_NAME } from '../../constants/stylesName/stylesName';
import { ModalContainerStandAlone } from './modalContainerStandAlone';
const ModalContainerControlledComponent = ({ variant, ctv, disableFocusableContent = false, ...props }, ref) => {
    const [hasScrolledPastTitle, setHasScrolledPastTitle] = React.useState(false);
    React.useEffect(() => {
        if (props.open) {
            setHasScrolledPastTitle(false);
        }
    }, [props.open]);
    const innerRef = React.useRef(null);
    const device = useMediaDevice();
    const styles = useStyles(STYLES_NAME.MODAL_CONTAINER, variant, ctv);
    const handleInnerRef = React.useCallback(node => {
        innerRef.current = node;
        if (!innerRef.current)
            return;
        const queryElement = (selector) => innerRef.current?.querySelector(selector);
        const modalActionsHeader = queryElement('[data-kbt-id="modal-container-actions-header"]');
        const modalContent = queryElement('[data-kbt-id="modal-container-content"]');
        const modalDraggableIcon = queryElement('[data-kbt-id="modal-container-draggable-icon"]');
        const modalTitle = queryElement('[data-kbt-id="modal-container-title"]');
        handleActionsHeaderShadowEffect(modalActionsHeader);
        handleContentVisibility({ container: innerRef.current, content: modalContent });
        handleContentScrollEffect(modalContent);
        handleContentScrollDetection(modalContent);
        handleDraggableIconSwipeDown(modalDraggableIcon);
        if (modalContent && modalTitle) {
            const images = modalTitle.querySelectorAll('img');
            const waitForImagesToLoad = Array.from(images).map(img => img.complete ? Promise.resolve() : new Promise(resolve => (img.onload = resolve)));
            Promise.all(waitForImagesToLoad).then(() => {
                attachScrollListener(modalContent, modalTitle);
            });
        }
    }, []);
    const attachScrollListener = (modalContent, modalTitle) => {
        const modalTitleHeight = modalTitle.offsetHeight;
        const handleScroll = () => {
            const scrollTop = modalContent.scrollTop;
            setHasScrolledPastTitle(scrollTop > modalTitleHeight);
        };
        modalContent.addEventListener('scroll', handleScroll);
        return () => {
            modalContent.removeEventListener('scroll', handleScroll);
        };
    };
    React.useImperativeHandle(ref, () => {
        return innerRef?.current;
    }, []);
    const isTabletOrMobile = React.useMemo(() => device !== DeviceBreakpointsType.DESKTOP && device !== DeviceBreakpointsType.LARGE_DESKTOP, [device]);
    const { scrollableRef: handleContentScrollEffect, shadowRef: handleActionsHeaderShadowEffect } = useScrollEffect({
        conditional: isTabletOrMobile,
        shadowStyles: styles.actionsAndHeaderContainer?.box_shadow,
    });
    const { handleContentVisibility } = useContentVisibility({});
    const { setPopoverRef: handlePopoverSwipeDown, setDragIconRef: handleDraggableIconSwipeDown } = useSwipeDown(props.popover?.animationOptions, () => props.onClose?.());
    const { hasScroll: contentHasScroll, handleScrollDetection: handleContentScrollDetection } = useScrollDetectionWithAutoFocus({
        parentElementRef: innerRef,
        disabled: disableFocusableContent,
    });
    return (_jsx(ModalContainerStandAlone, { ...props, ref: handleInnerRef, contentHasScroll: contentHasScroll, device: device, hasScrolledPastTitle: hasScrolledPastTitle, popover: { ...props.popover, forwardedRef: handlePopoverSwipeDown }, styles: styles }));
};
ModalContainerControlledComponent.displayName = 'ModalContainerControlledComponent';
const ModalContainerControlled = React.forwardRef(ModalContainerControlledComponent);
export { ModalContainerControlled };
//# sourceMappingURL=modalContainerControlled.js.map